import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { DownloadSimple, X, Export, Plus } from "@phosphor-icons/react";

const DISMISS_KEY = "theinner_install_dismissed";

function isStandalone() {
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}
function isMobile() {
  return /Android|iPhone|iPad|iPod/i.test(window.navigator.userAgent);
}
function isIOS() {
  return /iPhone|iPad|iPod/i.test(window.navigator.userAgent) && !window.MSStream;
}

// Show on the dashboard and right after finishing an assessment (result page).
function onTriggerRoute(pathname) {
  return pathname.startsWith("/dashboard") || pathname.startsWith("/results/");
}

export function InstallPrompt() {
  const location = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBIP);
    const onInstalled = () => { setShow(false); localStorage.setItem(DISMISS_KEY, "installed"); };
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  useEffect(() => {
    if (isStandalone() || !isMobile()) return;
    if (localStorage.getItem(DISMISS_KEY)) return;
    if (!onTriggerRoute(location.pathname)) { setShow(false); return; }
    // Android/Chrome: only if installable. iOS: always eligible (manual add).
    if (deferred || isIOS()) {
      const t = setTimeout(() => setShow(true), 1200);
      return () => clearTimeout(t);
    }
  }, [location.pathname, deferred]);

  const dismiss = () => {
    setShow(false);
    setIosHint(false);
    localStorage.setItem(DISMISS_KEY, "1");
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* noop */ }
      setDeferred(null);
      setShow(false);
      localStorage.setItem(DISMISS_KEY, "1");
    } else if (isIOS()) {
      setIosHint(true);
    }
  };

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
          className="fixed inset-x-3 bottom-[calc(5rem+env(safe-area-inset-bottom))] z-[60] lg:hidden"
          data-testid="install-prompt"
        >
          <div className="overflow-hidden rounded-2xl border border-teal-500/30 bg-slate-900/95 shadow-2xl backdrop-blur-xl">
            {!iosHint ? (
              <div className="flex items-center gap-3 p-3.5">
                <img src="/icon-192.png" alt="TheInner" className="h-11 w-11 shrink-0 rounded-xl" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-100">Add TheInner to your home screen</p>
                  <p className="truncate text-xs text-slate-400">Open it like an app — one tap, no store.</p>
                </div>
                <button onClick={install} className="shrink-0 rounded-full bg-teal-500 px-4 py-2 text-xs font-semibold text-slate-950 active:scale-95" data-testid="install-accept">
                  <span className="inline-flex items-center gap-1.5"><DownloadSimple size={14} weight="bold" /> Install</span>
                </button>
                <button onClick={dismiss} className="shrink-0 text-slate-500 active:text-slate-300" data-testid="install-dismiss" aria-label="Dismiss"><X size={18} /></button>
              </div>
            ) : (
              <div className="p-4" data-testid="install-ios-hint">
                <div className="flex items-start justify-between">
                  <p className="text-sm font-semibold text-slate-100">Install on iPhone</p>
                  <button onClick={dismiss} className="text-slate-500"><X size={18} /></button>
                </div>
                <ol className="mt-3 space-y-2 text-sm text-slate-300">
                  <li className="flex items-center gap-2">1. Tap the <Export size={16} className="text-teal-400" weight="bold" /> Share button in Safari</li>
                  <li className="flex items-center gap-2">2. Choose <Plus size={16} className="text-teal-400" weight="bold" /> “Add to Home Screen”</li>
                </ol>
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
